import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';

import { InputWithLabel, MultipleSelectWithChips, SEO } from 'main/components';

interface ProductOption {
  id: string;
  label: string;
}

const PRODUCT_OPTIONS: ProductOption[] = [
  { id: 'fpv-one-pilot', label: 'FPV.One Pilot' },
  { id: 'fpv-one-race', label: 'FPV.One Race' },
  { id: 'fpv-ctrl', label: 'FPV.Ctrl' },
  { id: 'dream-x', label: 'Dream X' },
  { id: 'drone-soccer', label: 'Drone Soccer' },
  { id: 'fpv-ace', label: 'FPV.Ace' },
  { id: 'power-meter-v2', label: 'Power Meter v2' },
  { id: 'lap-rf', label: 'LapRF' },
  { id: 'rapid-fire', label: 'Rapid Fire' },
  { id: 'skydive', label: 'SkyDive' },
];

export const ContactForm: React.FC = () => {
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [yourRequest, setYourRequest] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);

  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [data, setData] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const isMobile = windowWidth <= 991;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = 'Name field is required';
    if (!email.trim()) {
      newErrors.email = 'Email field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Value is not a valid email format';
    }
    if (!country.trim()) newErrors.country = 'Country field is required';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      setRequestError(null);

      try {
        const response = await fetch(
          'https://orqafpvsimulator-license.com/api/v1/asda/contact',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name,
              email,
              country,
              company,
              request: `Selected Products: ${selectedProducts.map(p => p.id)}${
                selectedProducts.length > 0 ? ',' : ''
              } description: ${yourRequest}`,
            }),
          },
        );

        if (response.ok) {
          setData(true);
          setErrors(null);
          setName('');
          setEmail('');
          setCompany('');
          setCountry('');
          setYourRequest('');
          setSelectedProducts([]);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Submission error:', error);
        setRequestError('Something went wrong, please try again later!');
        setData(null);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productParam = params.get('product');

    if (productParam) {
      const product = PRODUCT_OPTIONS.find(prod => prod.id === productParam);
      if (product) {
        setSelectedProducts([product]);
      }
    }
  }, [location.search]);

  return (
    <>
      <SEO seoTitle="Contact Us - Product Information & Support - Orqa" />
      <section className="page soccer-contact-form">
        <div className="wrapper wrapper--narrow contact-form-wrapper">
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              margin: '0px auto',
            }}
          >
            <h2
              style={{
                margin: '0 0 8px 0',
                color: '#FF2238',
                lineHeight: '1.25',
                paddingBottom: '8px',
              }}
              className="asda-contact-title"
            >
              Contact Us
            </h2>
            <p
              style={{ paddingBottom: '48px' }}
              className="asda-contact-description"
            >
              Have a question or want to learn more about our products? Fill out
              the form and we’ll get back to you with the information you need.
            </p>
            {data && !isMobile && (
              <div style={{ textAlign: 'left', marginBottom: '15px' }}>
                <p
                  style={{
                    fontSize: '14px',
                    color: requestError ? 'rgb(255, 34, 56)' : '#07bc0c',
                  }}
                >
                  {requestError || 'Form successfully submitted!'}
                </p>
              </div>
            )}
            <form
              id="asda-form"
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: isMobile ? 'column' : 'row',
                margin: '0 auto',
              }}
              onSubmit={handleSubmit}
            >
              {/* Input Fields Column */}
              <div
                className="asda__form-wrapper"
                style={{
                  flex: 1,
                  paddingRight: isMobile ? 0 : '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <InputWithLabel
                    required
                    setter={setName}
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'Name'}
                    errors={errors}
                    getter={name}
                  />
                  <InputWithLabel
                    required
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'Email'}
                    setter={setEmail}
                    errors={errors}
                    getter={email}
                  />
                  <InputWithLabel
                    required
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'Country'}
                    setter={setCountry}
                    errors={errors}
                    getter={country}
                  />
                  <InputWithLabel
                    setSelectedElement={setSelectedElement}
                    selectedElement={selectedElement}
                    name={'Company'}
                    setter={setCompany}
                    getter={company}
                  />
                  <MultipleSelectWithChips
                    selectedOptions={selectedProducts}
                    setSelectedOptions={setSelectedProducts}
                  />
                </div>
              </div>

              {!isMobile ? (
                <div
                  style={{
                    flex: 1,
                    paddingLeft: '12px',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      position: 'relative',
                    }}
                  >
                    <label
                      className={
                        selectedElement === 'Your request' || yourRequest
                          ? `asda-label contact-form-label asda-label--active asda-label--outside`
                          : `asda-label contact-form-label asda-label--outside`
                      }
                      style={{
                        position: 'absolute',
                        transform:
                          yourRequest || selectedElement === 'Your request'
                            ? 'scale(0.85)'
                            : '',
                        top:
                          yourRequest || selectedElement === 'Your request'
                            ? '-12px'
                            : '12px',
                        left: '12px',
                        transition: 'all 0.2s ease-out',
                        backgroundColor:
                          yourRequest || selectedElement === 'Your request'
                            ? 'black'
                            : 'transparent',
                        padding:
                          yourRequest || selectedElement === 'Your request'
                            ? '0 4px'
                            : '0',
                        zIndex: 1,
                        pointerEvents: 'none',
                        fontSize: '16px',
                        color:
                          selectedElement === 'Your request' || yourRequest
                            ? 'white'
                            : '#999',
                      }}
                    >
                      Your request
                    </label>
                    <textarea
                      value={yourRequest}
                      onChange={e => setYourRequest(e.target.value)}
                      className={'asda-input asda-textarea'}
                      style={{
                        flex: 1,
                        minHeight: '100%',
                        resize: 'none',
                        paddingTop: '16px',
                      }}
                      onFocus={() => setSelectedElement('Your request')}
                      onBlur={() => setSelectedElement(null)}
                    />
                    {errors?.yourrequest && (
                      <p className="asda__error__message">
                        {errors.yourrequest}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '24px', position: 'relative' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}
                  >
                    <label
                      className={
                        selectedElement === 'Your request' || yourRequest
                          ? `asda-label contact-form-label asda-label--active asda-label--outside`
                          : `asda-label contact-form-label asda-label--outside`
                      }
                      style={{
                        position: 'absolute',
                        transform:
                          yourRequest || selectedElement === 'Your request'
                            ? 'scale(0.85)'
                            : '',
                        top:
                          yourRequest || selectedElement === 'Your request'
                            ? '-12px'
                            : '12px',
                        left: '12px',
                        transition: 'all 0.2s ease-out',
                        backgroundColor:
                          yourRequest || selectedElement === 'Your request'
                            ? 'black'
                            : 'transparent',
                        padding:
                          yourRequest || selectedElement === 'Your request'
                            ? '0 4px'
                            : '0',
                        zIndex: 1,
                        pointerEvents: 'none',
                        fontSize: '16px',
                        color:
                          selectedElement === 'Your request' || yourRequest
                            ? 'white'
                            : '#999',
                      }}
                    >
                      Your request
                    </label>
                    <textarea
                      value={yourRequest}
                      onChange={e => setYourRequest(e.target.value)}
                      className={'asda-input asda-textarea'}
                      style={{
                        minHeight: '150px',
                        resize: 'none',
                        paddingTop: '16px',
                      }}
                      onFocus={() => setSelectedElement('Your request')}
                      onBlur={() => setSelectedElement(null)}
                    />
                    {errors?.yourrequest && (
                      <p className="asda__error__message">
                        {errors.yourrequest}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </form>
            <div
              style={{
                display: 'block',
                textAlign: 'left',
                marginTop: '25px',
              }}
            >
              <button
                type="submit"
                disabled={loading}
                className="asda__submit-button"
                onClick={handleSubmit}
              >
                {loading ? (
                  <div className="asda__button-spinner--wrapper">
                    <div className="asda__button-spinner" />
                  </div>
                ) : (
                  <p>Submit</p>
                )}
              </button>
            </div>

            {data && isMobile && (
              <div style={{ textAlign: 'left', marginTop: '15px' }}>
                <p
                  style={{
                    fontSize: '14px',
                    color: requestError ? 'rgb(255, 34, 56)' : '#07bc0c',
                  }}
                >
                  {requestError || 'Form successfully submitted!'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
